import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { OnboardingSupplementaryData } from '@/lib/onboarding'

const RELEVANT_SETTING_KEYS = [
  'branding_primary_color',
  'seo_meta_title',
  'seo_meta_description',
  'stripe_enabled',
  'stripe_publishable_key',
  'stripe_secret_key',
]

export function useOnboardingData(enabled: boolean = true) {
  return useQuery({
    queryKey: ['admin', 'onboarding-data'],
    queryFn: async (): Promise<OnboardingSupplementaryData> => {
      const [settingsRes, vehiclesRes, pricingRes, subscriptionsRes] = await Promise.all([
        supabase
          .from('account_settings')
          .select('account_id, key, value')
          .in('key', RELEVANT_SETTING_KEYS),
        supabase
          .from('vehicles')
          .select('account_id, id')
          .eq('is_active', true),
        supabase
          .from('vehicle_pricing')
          .select('account_id, id'),
        supabase
          .from('subscriptions')
          .select('account_id, status')
          .in('status', ['active', 'trialing']),
      ])

      const settingsByAccount = new Map<string, Map<string, string>>()
      for (const row of settingsRes.data ?? []) {
        if (!settingsByAccount.has(row.account_id)) {
          settingsByAccount.set(row.account_id, new Map())
        }
        if (row.value) {
          settingsByAccount.get(row.account_id)!.set(row.key, row.value)
        }
      }

      const activeVehicleCountByAccount = new Map<string, number>()
      for (const row of vehiclesRes.data ?? []) {
        const count = activeVehicleCountByAccount.get(row.account_id) ?? 0
        activeVehicleCountByAccount.set(row.account_id, count + 1)
      }

      const hasVehiclePricingByAccount = new Map<string, boolean>()
      for (const row of pricingRes.data ?? []) {
        hasVehiclePricingByAccount.set(row.account_id, true)
      }

      const hasActiveSubscriptionByAccount = new Map<string, boolean>()
      for (const row of subscriptionsRes.data ?? []) {
        hasActiveSubscriptionByAccount.set(row.account_id, true)
      }

      return {
        settingsByAccount,
        activeVehicleCountByAccount,
        hasVehiclePricingByAccount,
        hasActiveSubscriptionByAccount,
      }
    },
    enabled,
    staleTime: 1000 * 60 * 5,
  })
}
